import en from './locales/en.js';
import fr from './locales/fr.js';
import { getLanguage } from '../lib/utils.svelte.js';

/** @typedef {import('./locales/translationTypes.js').Translations} Translations */
/** @type {Record<string, Translations>} */
export const translations = { en, fr };

/**
 * @typedef {Object} I18nReturn
 * @property {() => string} locale - Getter for the current locale
 * @property {(key: string, params?: Record<string, any>) => string} t - Translation function
 * @property {(newLocale: string) => void} setLocale - Function to set a new locale
 * @property {() => void} initLocale - Function to initialize the locale
 * @property {(locale: string) => void} loadLocale - Function to load a new locale
 * @property {(key: string, specificLang: string) => string} translateInLang - Function to translate in a specific language
 */

/**
 * Creates an i18n instance
 * @returns {I18nReturn}
 */

function i18n() {
	/** @type {string} */
	// eslint-disable-next-line no-undef
	let locale = $state('fr');

	/**
	 * @returns {void}
	 */
	function initLocale() {
		const localStorageLanguage = getLanguage();
		if (localStorageLanguage && translations[localStorageLanguage]) {
			locale = localStorageLanguage;
		}
	}

	/**
	 * @param {Record<string, any>} obj
	 * @param {string} path
	 * @returns {any}
	 */
	function getNestedProperty(obj, path) {
		return path.split('.').reduce((prev, curr) => prev && prev[curr], obj);
	}

	/**
	 * @type {(key: string, params?: Record<string, any>) => string}
	 */
	const t = (key, params = {}) => {
		const value = getNestedProperty(translations[locale], key);
		if (value === undefined) {
			console.warn(`Missing translation: ${key}`);
			return key;
		}
		return value.replace(
			/\{(\w+)\}/g,
			/**
			 * @param {string} _
			 * @param {string} k
			 */
			(_, k) => params[k]?.toString() || `{${k}}`
		);
	};

	/**
	 * @param {string} newLocale
	 */
	function setLocale(newLocale) {
		if (translations[newLocale]) {
			locale = newLocale;
		} else {
			console.warn(`Locale not supported: ${newLocale}`);
		}
	}

	/**
	 * @param {string} locale
	 */
	async function loadLocale(locale) {
		const newLocale = await import(`$lib/locales/${locale}.js`);
		translations[locale] = newLocale;
	}

	/**
	 * Translate text in a specific language without changing the locale
	 * @param {string} key - Translation key
	 * @param {string} specificLang - Language to use for this translation
	 * @returns {string}
	 */
	const translateInLang = (key, specificLang) => {
		if (!translations[specificLang]) {
			console.warn(`Language not supported: ${specificLang}`);
			return t(key); // fallback to current locale
		}
		
		const value = getNestedProperty(translations[specificLang], key);
		if (value === undefined) {
			console.warn(`Missing translation: ${key}`);
			return key;
		}
		return value;
	};

	return {
		locale: () => locale,
		t,
		setLocale,
		initLocale,
		loadLocale,
		translateInLang
	};
}

export const { locale, t, setLocale, initLocale, loadLocale, translateInLang } = i18n();
